import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Message} from "primereact/message";
import {TabPanel, TabView} from "primereact/tabview";
import {Tag} from "primereact/tag";
import PageHeaderButton from "../components/page-header-button";
import {ADMIN_PERMISSIONS, hasAdminPermission, hasAiAccess} from "../../system/admin-access";
import {useContentTypeData} from "../../system/use-content-type-data";
import {useTranslate} from "../../system/useTranslate";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import {DataState, RTL_DATATABLE_PROPS, SalesSectionHeader} from "../sales/components/common";
import {statusSeverity} from "../sales/sales-utils";
import AdminAccessDenied from "../../system/components/admin-access-denied";
import MarkdownHelp from "../../system/components/markdown-help";
import {AiEngineConfigEditor, AiEngineEditor} from "./engine-editor";
import AiStructuredEntityEditor from "./structured-entity-editor";

const SECTIONS = [
    {
        labelKey: "ai-admin-tab-registry",
        types: [
            [CONTENT_TYPE_UIDS.AI_PROVIDER, "ai-admin-providers", true, "ai-admin-providers-help"],
            [CONTENT_TYPE_UIDS.AI_MODEL, "ai-admin-models", true, "ai-admin-models-help"],
            [CONTENT_TYPE_UIDS.AI_MODEL_PRICE, "ai-admin-prices", true, "ai-admin-prices-help"],
            [CONTENT_TYPE_UIDS.AI_ENGINE, "ai-admin-engines", true, "ai-admin-engines-help"],
        ],
    },
    {
        labelKey: "ai-admin-tab-orchestration",
        types: [
            [CONTENT_TYPE_UIDS.AI_PROMPT, "ai-admin-prompts", true, "ai-admin-prompts-help"],
            [CONTENT_TYPE_UIDS.AI_ENGINE_CONFIG, "ai-admin-engine-configs", true, "ai-admin-engine-configs-help"],
            [CONTENT_TYPE_UIDS.WORKSPACE_AI_SETTING, "ai-admin-workspace-settings", true, "ai-admin-workspace-settings-help"],
            [CONTENT_TYPE_UIDS.AI_BUDGET_POLICY, "ai-admin-budget-policies", true, "ai-admin-budget-policies-help"],
            [CONTENT_TYPE_UIDS.AI_RATE_LIMIT_POLICY, "ai-admin-rate-limit-policies", true, "ai-admin-rate-limit-policies-help"],
        ],
    },
    {
        labelKey: "ai-admin-tab-observability",
        types: [
            [CONTENT_TYPE_UIDS.AI_RUN, "ai-admin-runs", false, "ai-admin-runs-help"],
            [CONTENT_TYPE_UIDS.AI_USAGE_EVENT, "ai-admin-usage", false, "ai-admin-usage-help"],
            [CONTENT_TYPE_UIDS.AI_FEEDBACK, "ai-admin-feedback", false, "ai-admin-feedback-help"],
            [CONTENT_TYPE_UIDS.AI_PROPOSAL, "ai-admin-proposals", false, "ai-admin-proposals-help"],
            [CONTENT_TYPE_UIDS.AI_CREDIT_RESERVATION, "ai-admin-credit-reservations", false, "ai-admin-credit-reservations-help"],
            [CONTENT_TYPE_UIDS.CREDIT_WALLET, "ai-admin-credit-wallets", false, "ai-admin-credit-wallets-help"],
            [CONTENT_TYPE_UIDS.CREDIT_LEDGER_ENTRY, "ai-admin-credit-ledger", false, "ai-admin-credit-ledger-help"],
        ],
    },
];

function entityTitle(entity) {
    return entity.name || entity.title || entity.providerKey || entity.modelKey || entity.engineKey || entity.promptKey || entity.documentId || entity.id;
}

function entityStatus(entity) {
    return entity.providerStatus || entity.modelStatus || entity.engineStatus || entity.promptStatus || entity.configStatus || entity.runStatus || entity.proposalStatus || entity.feedbackStatus || (entity.aiEnabled === false ? "disabled" : "active");
}

function ContentTypePanel({uid, labelKey, editable}) {
    const translate = useTranslate();
    const data = useContentTypeData(uid);
    const ensure = data.ensure;
    const [selected, setSelected] = useState(null);
    const [visible, setVisible] = useState(false);
    const isEngine = uid === CONTENT_TYPE_UIDS.AI_ENGINE;
    const isEngineConfig = uid === CONTENT_TYPE_UIDS.AI_ENGINE_CONFIG;

    useEffect(() => {
        ensure({}, {all: true}).catch(() => undefined);
    }, [ensure]);

    const openEditor = (entity = null) => {
        setSelected(entity);
        setVisible(true);
    };

    return (
        <div className="flex flex-column gap-3">
            <SalesSectionHeader
                title={translate(labelKey)}
                description={translate(editable ? "ai-admin-configurable-description" : "ai-admin-readonly-description")}
                actions={<>
                    {editable && <Button className="text-xs gap-2" size="small" icon="pi pi-plus" label={translate("ai-admin-create")} onClick={() => openEditor()}/>}
                    <Button className="text-xs gap-2" size="small" text icon="pi pi-refresh" aria-label={translate("refresh")} loading={data.loading} onClick={() => data.reload({}, {all: true})}/>
                </>}
            />
            <DataState loading={data.loading} error={data.error} empty={!data.items.length}>
                <DataTable {...RTL_DATATABLE_PROPS} value={data.items} dataKey="documentId" size="small" paginator rows={15}>
                    <Column className="text-right" header={translate("ai-admin-name")} body={entityTitle}/>
                    <Column className="text-right" header={translate("ai-admin-key")} body={(row) => row.providerKey || row.modelKey || row.engineKey || row.promptKey || row.documentId}/>
                    <Column className="text-right" header={translate("ai-admin-status")} body={(row) => {
                        const status = entityStatus(row);
                        return <Tag className="text-xs" severity={statusSeverity(status)} value={translate(`ai-admin-status-${String(status).replace(/_/g, "-")}`)}/>;
                    }}/>
                    {editable && <Column className="text-right" header={translate("actions")} body={(row) => <Button className="text-xs gap-2" text rounded icon="pi pi-pencil" aria-label={translate("edit")} onClick={() => openEditor(row)}/>}/>}
                </DataTable>
            </DataState>
            {isEngine ? (
                <AiEngineEditor
                    visible={visible}
                    entity={selected}
                    data={data}
                    onHide={() => setVisible(false)}
                />
            ) : isEngineConfig ? (
                <AiEngineConfigEditor
                    visible={visible}
                    entity={selected}
                    data={data}
                    onHide={() => setVisible(false)}
                />
            ) : (
                <AiStructuredEntityEditor
                    uid={uid}
                    visible={visible}
                    entity={selected}
                    data={data}
                    onHide={() => setVisible(false)}
                />
            )}
        </div>
    );
}

function AiAdmin() {
    const translate = useTranslate();
    const {user} = useSelector((state) => state.auth);
    const [activeSection, setActiveSection] = useState(0);
    const [activeTypes, setActiveTypes] = useState({});
    const visibleSections = useMemo(() => SECTIONS.filter((item) => {
        const permissionByLabel = {
            "ai-admin-tab-registry": "ai.tab.registry",
            "ai-admin-tab-orchestration": "ai.tab.orchestration",
            "ai-admin-tab-observability": "ai.tab.observability",
        };
        return hasAdminPermission(user, permissionByLabel[item.labelKey]);
    }), [user]);
    const section = visibleSections[activeSection] || visibleSections[0];

    if (!hasAiAccess(user)) {
        return <AdminAccessDenied
            titleKey="ai-admin-access-denied"
            descriptionKey="ai-admin-access-denied-description"
        />;
    }

    if (!section) return <AdminAccessDenied/>;

    return (
        <div className="w-full p-3 flex flex-column gap-3 rtl text-xs">
            <PageHeaderButton title="ai-admin-title" description="ai-admin-description"/>
            <Message className="text-xs" severity="info" text={translate("ai-admin-secret-notice")}/>
            <TabView activeIndex={activeSection} onTabChange={(event) => setActiveSection(event.index)} className="rtl text-xs">
                {visibleSections.map((item) => <TabPanel key={item.labelKey} header={translate(item.labelKey)}/>)}
            </TabView>
            <TabView
                activeIndex={activeTypes[activeSection] || 0}
                onTabChange={(event) => setActiveTypes((current) => ({...current, [activeSection]: event.index}))}
                className="rtl text-xs"
            >
                {section.types.map(([uid, labelKey, editable, helpKey], index) => (
                    <TabPanel key={uid} header={translate(labelKey)}>
                        {(activeTypes[activeSection] || 0) === index && <>
                            <ContentTypePanel uid={uid} labelKey={labelKey} editable={editable && hasAdminPermission(user, ADMIN_PERMISSIONS.AI_CONFIGURE)}/>
                            <MarkdownHelp contentKey={helpKey}/>
                        </>}
                    </TabPanel>
                ))}
            </TabView>
        </div>
    );
}

export default AiAdmin;
